import React, { useEffect, useState } from 'react';
import '../styles/ProModeOnboarding.css';

// Versioned key — bump the suffix to re-surface the dialog to everyone after
// significant pro-mode UX changes. Bumping invalidates prior dismissals so
// returning users see the updated card set exactly once.
const STORAGE_KEY = 'chessKingdomProModeOnboardingSeen_v2';

/**
 * First-visit onboarding dialog introducing Fight to be King Professional Modes.
 * Appears once per device (LocalStorage-gated) and can be dismissed permanently.
 * Can also be triggered manually via the `forceOpen` prop.
 */
const ProModeOnboarding = ({ forceOpen = false, onClose = () => {} }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      return;
    }
    // Explicit replay via ?show_pro_onboarding=1 — bypasses the "seen" flag
    // so support can always demo the modal to a returning user.
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('show_pro_onboarding') === '1') {
        setOpen(true);
        return;
      }
    } catch { /* ignore */ }

    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (seen) return;
      // Sequence after LobbyTutorial — if the new-user tutorial hasn't completed yet,
      // wait. The tutorial component writes 'chess_kingdom_tutorial_completed' to
      // sessionStorage on finish; poll briefly so we don't stack two overlays.
      const tutorialDone = sessionStorage.getItem('chess_kingdom_tutorial_completed') === 'true';
      if (tutorialDone) {
        setOpen(true);
        return;
      }
      // Safety net: open after 12s even if the tutorial flag never lands
      // (user dismissed the tutorial in a way that bypassed completeTutorial,
      // or sessionStorage is blocked). The user still gets to see Pro mode.
      const safetyTimer = setTimeout(() => setOpen(true), 12000);
      const poll = setInterval(() => {
        if (sessionStorage.getItem('chess_kingdom_tutorial_completed') === 'true') {
          clearInterval(poll);
          clearTimeout(safetyTimer);
          setOpen(true);
        }
      }, 500);
      return () => { clearInterval(poll); clearTimeout(safetyTimer); };
    } catch {
      // localStorage may be blocked in private mode — silently skip
    }
  }, [forceOpen]);

  const dismiss = (markSeen = true) => {
    if (markSeen) {
      try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* ignore */ }
    }
    setOpen(false);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="pro-onboarding-overlay" onClick={() => dismiss(true)} data-testid="pro-onboarding-overlay">
      <div className="pro-onboarding-modal" onClick={(e) => e.stopPropagation()} data-testid="pro-onboarding-modal">
        <div className="pro-onboarding-header">
          <span className="pro-onboarding-badge">NEW</span>
          <h2>👑 Fight to be King — Professional Mode</h2>
          <button
            className="pro-onboarding-close"
            onClick={() => dismiss(true)}
            aria-label="Close"
            data-testid="pro-onboarding-close"
          >×</button>
        </div>

        <p className="pro-onboarding-intro">
          A deeper, more strategic take on Fight to be King. When your King falls, your Duke
          becomes restricted: it keeps only its diagonal powers. Reclaim the throne from your own baseline.
        </p>

        <div className="pro-onboarding-grid">
          <div className="pro-card pro-card-1">
            <div className="pro-card-icon">⚔️</div>
            <h3>Classic</h3>
            <p>The original Fight to be King — Duke keeps all captures.</p>
          </div>
          <div className="pro-card pro-card-2">
            <div className="pro-card-icon">👑</div>
            <h3>Pro 1 — Aggressive</h3>
            <p>Any move to the baseline reclaims the throne, even a capture.</p>
          </div>
          <div className="pro-card pro-card-3">
            <div className="pro-card-icon">🛡️</div>
            <h3>Pro 2 — Defensive</h3>
            <p>Only non-capture baseline moves transform. Duke may NOT touch the opponent's baseline.</p>
          </div>
        </div>

        <ul className="pro-onboarding-bullets">
          <li><span className="pro-dot pro-dot-orange" /> A pulsing orange ring marks the restricted Duke.</li>
          <li><span className="pro-dot pro-dot-red" /> Red check ring stacks with orange when the Duke is in check.</li>
          <li>🎯 Mix per-side Pro modes for asymmetric duels in the New Game panel.</li>
          <li>🏆 Each Pro mode has its own ranked ELO ladder — separate from Classic.</li>
        </ul>

        <div className="pro-onboarding-actions">
          <button
            className="pro-onboarding-btn pro-onboarding-btn-secondary"
            onClick={() => dismiss(true)}
            data-testid="pro-onboarding-later"
          >
            Got it, later
          </button>
          <a
            href="#rules"
            className="pro-onboarding-btn pro-onboarding-btn-primary"
            onClick={(e) => {
              e.preventDefault();
              dismiss(true);
              // Navigate to rules screen via custom event (consumer wires up)
              window.dispatchEvent(new CustomEvent('chessKingdomOpenRules', { detail: { section: 'pro-mode' } }));
            }}
            data-testid="pro-onboarding-learn"
          >
            Learn more →
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProModeOnboarding;
